import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { conversationService } from '../services/conversationService';
import { agentsService } from '../services/agentsService';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../components/ui/select';
import { MessageSquare, Plus, X } from 'lucide-react';
import { StatusBadge } from '../components/ui/status-badge';

// Format date only
const formatDateOnly = (timestamp) => {
  if (!timestamp) return 'Unknown';
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Format time only
const formatTimeOnly = (timestamp) => {
  if (!timestamp) return 'Unknown';
  return new Date(timestamp * 1000).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const Conversations = () => {
  const [conversations, setConversations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);
  const navigate = useNavigate();

  // Fetch agents for filter dropdown
  const fetchAgents = async () => {
    try {
      const data = await agentsService.getAgents();
      setAgents(data.agents || []);
    } catch (err) {
      // Error fetching agents
    }
  };

  // Handle adding a filter
  const addFilter = (type, value) => {
    if (!value) return;
    
    // Define filter order for consistency
    const filterOrder = ['agent', 'language', 'status'];
    
    // If filter type already exists, update it in place
    if (activeFilters.some(f => f.type === type)) {
      setActiveFilters(prev => 
        prev.map(f => f.type === type ? { type, value } : f)
      );
    } else {
      // Add new filter and sort by predefined order
      setActiveFilters(prev => {
        const newFilters = [...prev, { type, value }];
        return newFilters.sort((a, b) => {
          const indexA = filterOrder.indexOf(a.type);
          const indexB = filterOrder.indexOf(b.type);
          return indexA - indexB;
        });
      });
    }
  };

  // Handle removing a filter
  const removeFilter = (index) => {
    setActiveFilters(prev => prev.filter((_, i) => i !== index));
  };

  // Filter conversations based on active filters
  const filteredConversations = conversations.filter(conversation => {
    return activeFilters.every(filter => {
      switch (filter.type) {
        case 'agent':
          return conversation.agent_name === filter.value;
        case 'language':
          return filter.value === 'English'; // Since all are English
        case 'status':
          const status = conversation.call_successful === "success" ? "Successful" : "Failed";
          return status === filter.value;
        default:
          return true;
      }
    });
  });

  useEffect(() => {
    fetchConversations();
    fetchAgents();
  }, []);

  const fetchConversations = async (cursor = null) => {
    setIsLoading(true);
    try {
      const response = await conversationService.getConversations({
        cursor: cursor || undefined,
        limit: 20
      });

      const items = response.conversations || [];
      
      setConversations(prevConversations =>
        cursor ? [...prevConversations, ...items] : items
      );
      setHasMore(false); // Set based on actual API response if available
      setNextCursor(null); // Set based on actual API response if available
    } catch (err) {
      setError("Failed to load conversations. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Format duration from seconds to mm:ss
  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };



  const handleRowClick = (conversationId) => {
    navigate(`/conversations/${conversationId}`);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-card-foreground mb-6">
            Call History
          </h1>
          <div className="bg-destructive/10 text-destructive p-6 rounded-lg border border-destructive/20">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background rounded-tl-lg -mr-8">
      {/* Header with enhanced styling */}
      <div className="bg-background border-b border-border sticky top-0 z-10 rounded-tl-lg">
        <div className="flex h-16 items-center justify-between px-8">
          <div className="space-y-1">
            <h1 className="text-xl font-bold text-card-foreground tracking-tight">Conversations</h1>
          </div>
        </div>
      </div>

      <div className="px-8 py-8 space-y-8">

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Agent Filter - Always in first position */}
          {activeFilters.some(f => f.type === 'agent') ? (
            <div className="inline-flex items-center gap-2 transition-all duration-700 ease-out animate-in slide-in-from-left-5">
              <StatusBadge
                leftIcon={() => (
                  <button
                    onClick={() => removeFilter(activeFilters.findIndex(f => f.type === 'agent'))}
                    className="text-muted-foreground hover:text-foreground transition-colors duration-300"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
                leftLabel="Agent"
                rightLabel={activeFilters.find(f => f.type === 'agent')?.value || ''}
                className="h-8"
              />
            </div>
          ) : (
            <Select onValueChange={(value) => addFilter('agent', value)}>
              <SelectTrigger className="w-auto bg-background border border-border h-8 px-2 pr-1">
                <div className="inline-flex items-center mr-3">
                  <Plus className="h-3 w-3 mr-1.5 text-muted-foreground" />
                  <span className="text-xs text-card-foreground font-medium">Agent</span>
                </div>
              </SelectTrigger>
              <SelectContent
                align="start"
                sideOffset={8}
                alignOffset={0}
                className="w-[200px]"
              >
                {agents.map(agent => (
                  <SelectItem key={agent.agent_id} value={agent.name} className="text-left">{agent.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Language Filter - Always in second position */}
          {activeFilters.some(f => f.type === 'language') ? (
            <div className="inline-flex items-center gap-2 transition-all duration-700 ease-out animate-in slide-in-from-left-5">
              <StatusBadge
                leftIcon={() => (
                  <button
                    onClick={() => removeFilter(activeFilters.findIndex(f => f.type === 'language'))}
                    className="text-muted-foreground hover:text-foreground transition-colors duration-300"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
                leftLabel="Language"
                rightLabel={activeFilters.find(f => f.type === 'language')?.value || ''}
                className="h-8"
              />
            </div>
          ) : (
            <Select onValueChange={(value) => addFilter('language', value)}>
              <SelectTrigger className="w-auto bg-background border border-border h-8 px-2 pr-1">
                <div className="inline-flex items-center mr-3">
                  <Plus className="h-3 w-3 mr-1.5 text-muted-foreground" />
                  <span className="text-xs text-card-foreground font-medium">Language</span>
                </div>
              </SelectTrigger>
              <SelectContent
                align="start"
                sideOffset={8}
                alignOffset={0}
                className="w-[140px]"
              >
                <SelectItem value="English" className="text-left">English</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* Status Filter - Always in third position */}
          {activeFilters.some(f => f.type === 'status') ? (
            <div className="inline-flex items-center gap-2 transition-all duration-700 ease-out animate-in slide-in-from-left-5">
              <StatusBadge
                leftIcon={() => (
                  <button
                    onClick={() => removeFilter(activeFilters.findIndex(f => f.type === 'status'))}
                    className="text-muted-foreground hover:text-foreground transition-colors duration-300"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
                leftLabel="Status"
                rightLabel={activeFilters.find(f => f.type === 'status')?.value || ''}
                className="h-8"
              />
            </div>
          ) : (
            <Select onValueChange={(value) => addFilter('status', value)}>
              <SelectTrigger className="w-auto bg-background border border-border h-8 px-2 pr-1">
                <div className="inline-flex items-center mr-3">
                  <Plus className="h-3 w-3 mr-1.5 text-muted-foreground" />
                  <span className="text-xs text-card-foreground font-medium">Status</span>
                </div>
              </SelectTrigger>
              <SelectContent
                align="start"
                sideOffset={8}
                alignOffset={0}
                className="w-[140px]"
              >
                <SelectItem value="Successful" className="text-left">Successful</SelectItem>
                <SelectItem value="Failed" className="text-left">Failed</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Conversations Table */}
        <Card className="border border-border bg-card shadow-none">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border">
                  <TableHead className="text-muted-foreground font-semibold">Date</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Time</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Agent</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Duration</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Language</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Status</TableHead>
                </TableRow>
              </TableHeader>
                  <TableBody>
                    {isLoading && (!conversations || conversations.length === 0) ? (
                      // Skeleton loading rows
                      Array.from({ length: 5 }).map((_, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Skeleton className="h-4 w-[80px]" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-[60px]" />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-3">
                              <Skeleton className="h-10 w-10 rounded-xl" />
                              <div className="space-y-2">
                                <Skeleton className="h-4 w-[120px]" />
                                <Skeleton className="h-3 w-[80px]" />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-[80px]" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-4 w-[70px]" />
                          </TableCell>
                          <TableCell>
                            <Skeleton className="h-6 w-[80px] rounded-full" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : !filteredConversations || filteredConversations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="h-32 text-center">
                          <div className="flex flex-col items-center justify-center py-12">
                            <div className="w-16 h-16 bg-muted flex items-center justify-center mb-4">
                              <MessageSquare className="w-8 h-8 text-muted-foreground" />
                            </div>
                            <h3 className="text-xl font-bold text-card-foreground mb-2">No conversations found</h3>
                            <p className="text-muted-foreground">
                              No conversation history found
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (filteredConversations || []).map((conversation) => {
                        return (
                          <TableRow
                            key={conversation.conversation_id}
                            className="cursor-pointer hover:bg-muted/50 transition-colors border-b border-border"
                            onClick={() => handleRowClick(conversation.conversation_id)}
                          >
                            <TableCell className="font-medium text-card-foreground text-xs">
                              {formatDateOnly(conversation.start_time_unix_secs)}
                            </TableCell>
                            <TableCell className="font-medium text-card-foreground text-xs">
                              {formatTimeOnly(conversation.start_time_unix_secs)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center space-x-3">
                                <Avatar className="w-10 h-10">
                                  <AvatarFallback className="bg-accent text-accent-foreground font-semibold">
                                    {conversation.agent_name ? conversation.agent_name.charAt(0).toUpperCase() : 'A'}
                                  </AvatarFallback>
                                </Avatar>
                                <div>
                                  <div className="font-semibold text-card-foreground">{conversation.agent_name}</div>
                                  <div className="text-xs text-muted-foreground">
                                    ID: {conversation.conversation_id.substring(0, 8)}...
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-xs text-card-foreground bg-secondary px-3 py-1.5 rounded-md">
                                {formatDuration(conversation.call_duration_secs)}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="text-xs text-card-foreground">
                                English
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                conversation.call_successful === "success"
                                  ? 'bg-success-background text-success-foreground'
                                  : 'bg-destructive-background text-destructive'
                              }`}>
                                {conversation.call_successful === "success" ? "Successful" : "Failed"}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

        {/* Load more button */}
        {hasMore && (
          <div className="flex justify-center">
            <Button
              onClick={() => fetchConversations(nextCursor)}
              disabled={isLoading}
              variant="outline"
              size="lg"
              className="border-border text-muted-foreground hover:bg-muted"
            >
              Load More Conversations
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Conversations; 